import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
const uuid_v4 = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;

const PANEL_CONTAINER_SX = {
  height: "100%",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

const LIST_SCROLL_SX = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  pr: 1,
};

const LOAN_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    fontSize: "14px",
    "& fieldset": {
      borderColor: "#e2e8f0",
    },
    "&:hover fieldset": {
      borderColor: "#cbd5e0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4299e1",
      borderWidth: "1px",
    },
    "& input": {
      padding: "12px 14px",
    },
    "& textarea": {
      padding: "12px 14px",
    },
  },
};

const CARD_SX = {
  borderRadius: 3,
  border: "1px solid rgba(0,0,0,0.10)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  bgcolor: "#F8FAFC",
  overflow: "hidden",
};

const SECTION_LABEL_SX = {
  fontWeight: 700,
  fontSize: 11.5,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#64748b",
  mb: 1,
};

const ENTRY_CARD_SX = {
  borderRadius: 2,
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  p: 1.5,
};

const COLLAPSIBLE_SECTION_SX = {
  borderRadius: 2,
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  overflow: "hidden",
};

const HEADER_ICON_BUTTON_SX = {
  color: "#64748b",
  p: 0.35,
};

const HEADER_DELETE_BUTTON_SX = {
  ...HEADER_ICON_BUTTON_SX,
  "&:hover": {
    color: "#dc2626",
    backgroundColor: "#fee2e2",
  },
};

const makeLoanId = () => `loan_${uuid_v4()}`;
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const makeEmptyLoanAdjustment = () => ({
  effectiveDate: "",
  adjustedInterestRate: "",
  reason: "",
  notes: "",
});

const normalizeLoanAdjustment = (adjustment = {}) => ({
  effectiveDate: adjustment?.effectiveDate || "",
  adjustedInterestRate: adjustment?.adjustedInterestRate ?? "",
  reason: adjustment?.reason || "",
  notes: adjustment?.notes || "",
});

const serializeLoanAdjustment = (adjustment = {}) => {
  const normalized = normalizeLoanAdjustment(adjustment);
  return {
    effectiveDate: normalized.effectiveDate,
    adjustedInterestRate: normalized.adjustedInterestRate,
    reason: normalized.reason,
    notes: normalized.notes,
  };
};

const makeEmptyLoanNote = () => ({
  date: "",
  note: "",
});

const normalizeLoanNote = (noteEntry = {}) => {
  if (typeof noteEntry === "string") {
    return {
      date: "",
      note: noteEntry,
    };
  }

  return {
    date: noteEntry?.date || "",
    note: noteEntry?.note || "",
  };
};

const serializeLoanNote = (noteEntry = {}) => {
  const normalized = normalizeLoanNote(noteEntry);
  return {
    date: normalized.date,
    note: normalized.note,
  };
};

const makeEmptyLoan = (priority = "") => ({
  loanId: "",
  loanName: "",
  lenderName: "",
  loanType: "",
  priority,
  loanAmount: "",
  interestRate: "",
  rateType: "",
  interestOnlyMonths: "",
  currentBalance: "",
  termMonths: "",
  amortizationMonths: "",
  monthlyPayment: "",
  paymentDueDate: "",
  maturityDate: "",
  ltv: "",
  points: "",
  prepaymentPenalty: "",
  borrowerOwningEntity: "",
  guarantor: "",
  collateralSecuredProperty: "",
  servicerContactInfo: "",
  escrowReserveRequirements: "",
  covenantsSpecialConditions: "",
  relatedDocuments: "",
  notes: [],
  adjustments: [],
  firstLoanDate: "",
  loanBenefits: "",
});

const normalizeLoan = (loan = {}, fallbackId = "", fallbackPriority = "") => ({
  id: loan?.id || fallbackId || makeLoanId(),
  loanId: loan?.loanId || "",
  loanName: loan?.loanName || "",
  lenderName: loan?.lenderName || "",
  loanType: loan?.loanType || "",
  priority: loan?.priority ?? fallbackPriority,
  loanAmount: loan?.loanAmount ?? "",
  interestRate: loan?.interestRate ?? "",
  rateType: loan?.rateType || "",
  interestOnlyMonths: loan?.interestOnlyMonths ?? "",
  currentBalance: loan?.currentBalance ?? "",
  termMonths: loan?.termMonths ?? loan?.loanTermMonths ?? "",
  amortizationMonths: loan?.amortizationMonths ?? "",
  monthlyPayment: loan?.monthlyPayment ?? "",
  paymentDueDate: loan?.paymentDueDate || "",
  maturityDate: loan?.maturityDate || "",
  ltv: loan?.ltv ?? "",
  points: loan?.points ?? "",
  prepaymentPenalty: loan?.prepaymentPenalty || "",
  borrowerOwningEntity: loan?.borrowerOwningEntity || "",
  guarantor: loan?.guarantor || "",
  collateralSecuredProperty: loan?.collateralSecuredProperty || "",
  servicerContactInfo: loan?.servicerContactInfo || "",
  escrowReserveRequirements: loan?.escrowReserveRequirements || "",
  covenantsSpecialConditions: loan?.covenantsSpecialConditions || "",
  relatedDocuments: loan?.relatedDocuments || "",
  notes: Array.isArray(loan?.notes)
    ? loan.notes.map((noteEntry) => normalizeLoanNote(noteEntry))
    : String(loan?.notes || "").trim()
      ? [normalizeLoanNote({ note: String(loan.notes).trim() })]
      : [],
  adjustments: Array.isArray(loan?.adjustments)
    ? loan.adjustments.map((adjustment) => normalizeLoanAdjustment(adjustment))
    : [],
  firstLoanDate: loan?.firstLoanDate || loan?.originationDate || "",
  loanBenefits: loan?.loanBenefits || "",
});

const serializeLoan = (loan = {}, fallbackPriority = "") => {
  const normalized = normalizeLoan(loan, loan?.id, fallbackPriority);
  return {
    loanId: normalized.loanId,
    loanName: normalized.loanName,
    lenderName: normalized.lenderName,
    loanType: normalized.loanType,
    priority: normalized.priority,
    loanAmount: normalized.loanAmount,
    interestRate: normalized.interestRate,
    rateType: normalized.rateType,
    interestOnlyMonths: normalized.interestOnlyMonths,
    currentBalance: normalized.currentBalance,
    termMonths: normalized.termMonths,
    amortizationMonths: normalized.amortizationMonths,
    monthlyPayment: normalized.monthlyPayment,
    paymentDueDate: normalized.paymentDueDate,
    maturityDate: normalized.maturityDate,
    ltv: normalized.ltv,
    points: normalized.points,
    prepaymentPenalty: normalized.prepaymentPenalty,
    borrowerOwningEntity: normalized.borrowerOwningEntity,
    guarantor: normalized.guarantor,
    collateralSecuredProperty: normalized.collateralSecuredProperty,
    servicerContactInfo: normalized.servicerContactInfo,
    escrowReserveRequirements: normalized.escrowReserveRequirements,
    covenantsSpecialConditions: normalized.covenantsSpecialConditions,
    relatedDocuments: normalized.relatedDocuments,
    notes: normalized.notes.map((noteEntry) => serializeLoanNote(noteEntry)),
    adjustments: normalized.adjustments.map((adjustment) =>
      serializeLoanAdjustment(adjustment)
    ),
    firstLoanDate: normalized.firstLoanDate,
    loanBenefits: normalized.loanBenefits,
  };
};

const hasLegacyLoanShape = (loan = {}) => {
  if (hasOwn(loan, "id") || hasOwn(loan, "loanTermMonths")) {
    return true;
  }

  const requiredFields = [
    "loanId",
    "loanName",
    "lenderName",
    "loanType",
    "priority",
    "loanAmount",
    "interestRate",
    "rateType",
    "interestOnlyMonths",
    "currentBalance",
    "termMonths",
    "amortizationMonths",
    "monthlyPayment",
    "paymentDueDate",
    "maturityDate",
    "ltv",
    "points",
    "prepaymentPenalty",
    "borrowerOwningEntity",
    "guarantor",
    "collateralSecuredProperty",
    "servicerContactInfo",
    "escrowReserveRequirements",
    "covenantsSpecialConditions",
    "relatedDocuments",
    "notes",
    "adjustments",
    "firstLoanDate",
    "loanBenefits",
  ];

  if (requiredFields.some((field) => !hasOwn(loan, field))) {
    return true;
  }

  if (!Array.isArray(loan.notes) || !Array.isArray(loan.adjustments)) {
    return true;
  }

  if (
    loan.notes.some(
      (noteEntry) => !hasOwn(noteEntry, "date") || !hasOwn(noteEntry, "note")
    )
  ) {
    return true;
  }

  return loan.adjustments.some(
    (adjustment) =>
      !hasOwn(adjustment, "effectiveDate") ||
      !hasOwn(adjustment, "adjustedInterestRate") ||
      !hasOwn(adjustment, "reason") ||
      !hasOwn(adjustment, "notes")
  );
};

const formatCurrencyDisplay = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (normalized.startsWith("$")) return normalized;
  return `$${normalized}`;
};

const buildLoanSummary = (loan) => {
  const rateValue = [loan?.interestRate, loan?.rateType].filter(Boolean).join(" ");
  const balanceValue = formatCurrencyDisplay(loan?.currentBalance || loan?.loanAmount);
  return [
    {
      label: "Type",
      value: loan?.loanType,
    },
    {
      label: "Balance",
      value: balanceValue,
    },
    {
      label: "Rate",
      value: rateValue,
    },
    {
      label: "Maturity",
      value: loan?.maturityDate,
    },
  ].filter((item) => item.value);
};

const LoanFieldGroup = ({ title, collapsed, onToggle, children }) => (
  <Box sx={COLLAPSIBLE_SECTION_SX}>
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1.1,
        py: 0.75,
        minHeight: 38,
        backgroundColor: "#F3F4F6",
        borderBottom: collapsed ? "none" : "1px solid #E5E7EB",
        cursor: "pointer",
      }}
    >
      <Typography sx={{ ...SECTION_LABEL_SX, mb: 0 }}>{title}</Typography>
      <Tooltip title={collapsed ? `Expand ${title}` : `Collapse ${title}`}>
        <IconButton size="small" sx={HEADER_ICON_BUTTON_SX}>
          {collapsed ? (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
    {!collapsed ? <Box sx={{ p: 1.5 }}>{children}</Box> : null}
  </Box>
);

const CollapsibleLoanSection = ({
  title,
  collapsed,
  onToggle,
  onAdd,
  addTitle,
  children,
}) => (
  <Box sx={COLLAPSIBLE_SECTION_SX}>
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1.1,
        py: 0.75,
        minHeight: 38,
        backgroundColor: "#F3F4F6",
        borderBottom: collapsed ? "none" : "1px solid #E5E7EB",
        cursor: "pointer",
      }}
    >
      <Typography sx={{ ...SECTION_LABEL_SX, mb: 0 }}>{title}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title={addTitle}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: "#e0f2fe",
              color: "#0369a1",
              "&:hover": {
                backgroundColor: "#bae6fd",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={collapsed ? `Expand ${title}` : `Collapse ${title}`}>
          <IconButton size="small" sx={HEADER_ICON_BUTTON_SX}>
            {collapsed ? (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>

    {!collapsed ? <Box sx={{ p: 1.5 }}>{children}</Box> : null}
  </Box>
);

const PropertyLoansTab = forwardRef(
  ({ propertyInfo, updatePropertyInfo, externalToolbar = false }, ref) => {
  const hasPropertyInfo = Boolean(propertyInfo);
  const rawLoans = useMemo(
    () =>
      Array.isArray(propertyInfo?.details?.loans)
        ? propertyInfo.details.loans
        : [],
    [propertyInfo?.details?.loans]
  );
  const loans = useMemo(
    () =>
      rawLoans.map((loan, index) =>
        normalizeLoan(loan, `loan_${index}`, index + 1)
      ),
    [rawLoans]
  );
  const [collapsedById, setCollapsedById] = useState({});
  const [sectionCollapsedById, setSectionCollapsedById] = useState({});
  const [editingLoanNameId, setEditingLoanNameId] = useState("");
  const [editingLoanNameDraft, setEditingLoanNameDraft] = useState("");

  useEffect(() => {
    if (!hasPropertyInfo) return;
    if (Array.isArray(propertyInfo?.details?.loans)) return;
    updatePropertyInfo("details.loans", []);
  }, [hasPropertyInfo, propertyInfo?.details?.loans, updatePropertyInfo]);

  useEffect(() => {
    if (rawLoans.length === 0) return;
    if (!rawLoans.some((loan) => hasLegacyLoanShape(loan))) return;
    updatePropertyInfo(
      "details.loans",
      rawLoans.map((loan, index) => serializeLoan(loan, index + 1))
    );
  }, [rawLoans, updatePropertyInfo]);

  useEffect(() => {
    setCollapsedById((prevState) => {
      const nextState = {};
      loans.forEach((loan) => {
        nextState[loan.id] = Object.prototype.hasOwnProperty.call(prevState, loan.id)
          ? prevState[loan.id]
          : false;
      });
      return nextState;
    });
  }, [loans]);

  useEffect(() => {
    setSectionCollapsedById((prevState) => {
      const nextState = {};
      loans.forEach((loan) => {
        const previousSectionState = prevState[loan.id] || {};
        nextState[loan.id] = {
          identity: hasOwn(previousSectionState, "identity")
            ? previousSectionState.identity
            : false,
          terms: hasOwn(previousSectionState, "terms")
            ? previousSectionState.terms
            : false,
          parties: hasOwn(previousSectionState, "parties")
            ? previousSectionState.parties
            : false,
          servicing: hasOwn(previousSectionState, "servicing")
            ? previousSectionState.servicing
            : false,
          adjustments: hasOwn(previousSectionState, "adjustments")
            ? previousSectionState.adjustments
            : loan.adjustments.length === 0,
          notes: hasOwn(previousSectionState, "notes")
            ? previousSectionState.notes
            : loan.notes.length === 0,
        };
      });
      return nextState;
    });
  }, [loans]);

  useEffect(() => {
    if (!editingLoanNameId) return;
    if (loans.some((loan) => loan.id === editingLoanNameId)) return;
    setEditingLoanNameId("");
    setEditingLoanNameDraft("");
  }, [loans, editingLoanNameId]);

  const syncLoans = (nextLoans) => {
    updatePropertyInfo(
      "details.loans",
      nextLoans.map((loan, index) => serializeLoan(loan, index + 1))
    );
  };

  const setLoanSectionCollapsed = (loanId, sectionKey, collapsed) => {
    setSectionCollapsedById((prevState) => ({
      ...prevState,
      [loanId]: {
        ...(prevState[loanId] || {}),
        [sectionKey]: collapsed,
      },
    }));
  };

  const toggleLoanSection = (loanId, sectionKey) => {
    setSectionCollapsedById((prevState) => ({
      ...prevState,
      [loanId]: {
        ...(prevState[loanId] || {}),
        [sectionKey]: !prevState[loanId]?.[sectionKey],
      },
    }));
  };

  const addLoan = () => {
    const newLoan = makeEmptyLoan(loans.length + 1);
    syncLoans([...loans, newLoan]);
    setCollapsedById((prevState) => ({
      ...prevState,
      [newLoan.id]: true,
    }));
    setSectionCollapsedById((prevState) => ({
      ...prevState,
      [newLoan.id]: {
        ...(prevState[newLoan.id] || {}),
        identity: true,
        terms: true,
        parties: true,
        servicing: true,
        adjustments: true,
        notes: true,
      },
    }));
  };

  useImperativeHandle(
    ref,
    () => ({
      addLoan,
    }),
    [loans.length]
  );

  const updateLoan = (loanId, field, value) => {
    syncLoans(
      loans.map((loan) =>
        loan.id === loanId ? { ...loan, [field]: value } : loan
      )
    );
  };

  const addLoanAdjustment = (loanId) => {
    syncLoans(
      loans.map((loan) =>
        loan.id === loanId
          ? {
              ...loan,
              adjustments: [
                ...(Array.isArray(loan.adjustments) ? loan.adjustments : []),
                makeEmptyLoanAdjustment(),
              ],
            }
          : loan
      )
    );
    setLoanSectionCollapsed(loanId, "adjustments", false);
  };

  const updateLoanAdjustment = (loanId, adjustmentIndex, field, value) => {
    syncLoans(
      loans.map((loan) => {
        if (loan.id !== loanId) return loan;
        const nextAdjustments = Array.isArray(loan.adjustments)
          ? [...loan.adjustments]
          : [];
        nextAdjustments[adjustmentIndex] = {
          ...normalizeLoanAdjustment(nextAdjustments[adjustmentIndex]),
          [field]: value,
        };
        return {
          ...loan,
          adjustments: nextAdjustments,
        };
      })
    );
  };

  const deleteLoanAdjustment = (loanId, adjustmentIndex) => {
    const targetLoan = loans.find((loan) => loan.id === loanId);
    const nextAdjustmentCount = Math.max(
      0,
      (targetLoan?.adjustments?.length || 0) - 1
    );

    syncLoans(
      loans.map((loan) =>
        loan.id === loanId
          ? {
              ...loan,
              adjustments: (loan.adjustments || []).filter(
                (_, index) => index !== adjustmentIndex
              ),
            }
          : loan
      )
    );

    if (nextAdjustmentCount === 0) {
      setLoanSectionCollapsed(loanId, "adjustments", true);
    }
  };

  const addLoanNote = (loanId) => {
    syncLoans(
      loans.map((loan) =>
        loan.id === loanId
          ? {
              ...loan,
              notes: [
                ...(Array.isArray(loan.notes) ? loan.notes : []),
                makeEmptyLoanNote(),
              ],
            }
          : loan
      )
    );
    setLoanSectionCollapsed(loanId, "notes", false);
  };

  const updateLoanNote = (loanId, noteIndex, field, value) => {
    syncLoans(
      loans.map((loan) => {
        if (loan.id !== loanId) return loan;
        const nextNotes = Array.isArray(loan.notes) ? [...loan.notes] : [];
        nextNotes[noteIndex] = {
          ...normalizeLoanNote(nextNotes[noteIndex]),
          [field]: value,
        };
        return {
          ...loan,
          notes: nextNotes,
        };
      })
    );
  };

  const deleteLoanNote = (loanId, noteIndex) => {
    const targetLoan = loans.find((loan) => loan.id === loanId);
    const nextNoteCount = Math.max(0, (targetLoan?.notes?.length || 0) - 1);

    syncLoans(
      loans.map((loan) =>
        loan.id === loanId
          ? {
              ...loan,
              notes: (loan.notes || []).filter((_, index) => index !== noteIndex),
            }
          : loan
      )
    );

    if (nextNoteCount === 0) {
      setLoanSectionCollapsed(loanId, "notes", true);
    }
  };

  const deleteLoan = (loanId) => {
    syncLoans(loans.filter((loan) => loan.id !== loanId));
    setCollapsedById((prevState) => {
      const nextState = { ...prevState };
      delete nextState[loanId];
      return nextState;
    });
    setSectionCollapsedById((prevState) => {
      const nextState = { ...prevState };
      delete nextState[loanId];
      return nextState;
    });
  };

  const toggleLoan = (loanId) => {
    setCollapsedById((prevState) => ({
      ...prevState,
      [loanId]: !prevState[loanId],
    }));
  };

  const startEditingLoanName = (loanId, displayName) => {
    setEditingLoanNameId(loanId);
    setEditingLoanNameDraft(displayName);
  };

  const commitLoanNameEdit = (loanId, fallbackName) => {
    const trimmedName = String(editingLoanNameDraft || "").trim();
    updateLoan(
      loanId,
      "loanName",
      trimmedName && trimmedName !== fallbackName ? trimmedName : ""
    );
    setEditingLoanNameId("");
    setEditingLoanNameDraft("");
  };

  const cancelLoanNameEdit = () => {
    setEditingLoanNameId("");
    setEditingLoanNameDraft("");
  };

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", pb: 1 }}>
      <Box sx={PANEL_CONTAINER_SX}>
        {!externalToolbar ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceIcon sx={{ fontSize: 18, color: "#334155" }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#334155",
                }}
              >
                Loans
              </Typography>
            </Box>
            <Tooltip title="Add loan">
              <IconButton
                size="small"
                onClick={addLoan}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: "#3b82f6",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        <Box sx={LIST_SCROLL_SX}>
          {loans.length === 0 ? (
            <Box
              sx={{
                ...CARD_SX,
                minHeight: "100%",
                p: 3,
                textAlign: "center",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                  No loans added yet.
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Use the plus button{externalToolbar ? " in the action bar" : ""} to add a loan card for this property.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Stack spacing={2}>
            {loans.map((loan, index) => {
              const isCollapsed = Boolean(collapsedById[loan.id]);
              const summaryItems = buildLoanSummary(loan);
              const sectionState = sectionCollapsedById[loan.id] || {};
              const identityCollapsed = Boolean(sectionState.identity);
              const termsCollapsed = Boolean(sectionState.terms);
              const partiesCollapsed = Boolean(sectionState.parties);
              const servicingCollapsed = Boolean(sectionState.servicing);
              const adjustmentsCollapsed = Boolean(sectionState.adjustments);
              const notesCollapsed = Boolean(sectionState.notes);

              return (
                <Box key={loan.id} sx={CARD_SX}>
                  <Box
                    onClick={() => toggleLoan(loan.id)}
                    sx={{
                      px: 1.5,
                      py: 0.9,
                      minHeight: 44,
                      backgroundColor: "#E5E7EB",
                      borderBottom: isCollapsed ? "none" : "1px solid #CBD5E1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <Box
                          onClick={(event) => event.stopPropagation()}
                          onDoubleClick={() =>
                            startEditingLoanName(
                              loan.id,
                              loan.loanName || `Loan ${index + 1}`
                            )
                          }
                          sx={{ minWidth: 0, maxWidth: "100%", cursor: "text" }}
                        >
                          {editingLoanNameId === loan.id ? (
                            <TextField
                              autoFocus
                              size="small"
                              value={editingLoanNameDraft}
                              onChange={(event) =>
                                setEditingLoanNameDraft(event.target.value)
                              }
                              onBlur={() =>
                                commitLoanNameEdit(loan.id, `Loan ${index + 1}`)
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  commitLoanNameEdit(loan.id, `Loan ${index + 1}`);
                                }
                                if (event.key === "Escape") {
                                  event.preventDefault();
                                  cancelLoanNameEdit();
                                }
                              }}
                              sx={{
                                ...LOAN_FIELD_SX,
                                width: { xs: "100%", sm: 220 },
                                "& .MuiOutlinedInput-root": {
                                  ...LOAN_FIELD_SX["& .MuiOutlinedInput-root"],
                                  backgroundColor: "#ffffff",
                                  fontSize: "16px",
                                  "& input": {
                                    textAlign: "left",
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Tooltip title="Double-click to rename loan">
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 18,
                                  lineHeight: 1.2,
                                  letterSpacing: "0.04em",
                                  color: "#334155",
                                  mb: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {loan.loanName || `Loan ${index + 1}`}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ minWidth: 0 }}
                        >
                          {summaryItems.map((item) => (
                            <Box
                              key={`${loan.id}-${item.label}`}
                              sx={{
                                borderRadius: "999px",
                                backgroundColor: item.muted ? "#e2e8f0" : "#dbeafe",
                                color: item.muted ? "#64748b" : "#1d4ed8",
                                px: 1,
                                py: 0.5,
                                fontSize: 12,
                                lineHeight: 1.2,
                                maxWidth: "100%",
                              }}
                            >
                              {item.label ? (
                                <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                                  {item.label}:
                                </Box>
                              ) : null}
                              <Box component="span">{item.value}</Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Tooltip title="Delete loan">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); deleteLoan(loan.id); }}
                          sx={HEADER_DELETE_BUTTON_SX}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isCollapsed ? "Expand loan" : "Collapse loan"}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); toggleLoan(loan.id); }}
                          sx={HEADER_ICON_BUTTON_SX}
                        >
                          {isCollapsed ? (
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ExpandLessIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {!isCollapsed ? (
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <LoanFieldGroup
                          title="Loan Identity"
                          collapsed={identityCollapsed}
                          onToggle={() => toggleLoanSection(loan.id, "identity")}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Lender Name"
                                value={loan.lenderName}
                                onChange={(event) =>
                                  updateLoan(loan.id, "lenderName", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Loan Type"
                                value={loan.loanType}
                                onChange={(event) =>
                                  updateLoan(loan.id, "loanType", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Loan ID"
                                value={loan.loanId}
                                onChange={(event) =>
                                  updateLoan(loan.id, "loanId", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </LoanFieldGroup>

                        <LoanFieldGroup
                          title="Terms & Dates"
                          collapsed={termsCollapsed}
                          onToggle={() => toggleLoanSection(loan.id, "terms")}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Original Loan Amount"
                                value={loan.loanAmount}
                                onChange={(event) =>
                                  updateLoan(loan.id, "loanAmount", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Current Balance"
                                value={loan.currentBalance}
                                onChange={(event) =>
                                  updateLoan(loan.id, "currentBalance", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Monthly Payment"
                                value={loan.monthlyPayment}
                                onChange={(event) =>
                                  updateLoan(loan.id, "monthlyPayment", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Interest Rate"
                                value={loan.interestRate}
                                onChange={(event) =>
                                  updateLoan(loan.id, "interestRate", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Rate Type"
                                value={loan.rateType}
                                onChange={(event) =>
                                  updateLoan(loan.id, "rateType", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Interest Only Months"
                                value={loan.interestOnlyMonths}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "interestOnlyMonths",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Term Months"
                                value={loan.termMonths}
                                onChange={(event) =>
                                  updateLoan(loan.id, "termMonths", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Amortization Months"
                                value={loan.amortizationMonths}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "amortizationMonths",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Priority"
                                value={loan.priority}
                                onChange={(event) =>
                                  updateLoan(loan.id, "priority", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Origination Date"
                                type="date"
                                value={loan.firstLoanDate}
                                onChange={(event) =>
                                  updateLoan(loan.id, "firstLoanDate", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Payment Due Date"
                                type="date"
                                value={loan.paymentDueDate}
                                onChange={(event) =>
                                  updateLoan(loan.id, "paymentDueDate", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Maturity Date"
                                type="date"
                                value={loan.maturityDate}
                                onChange={(event) =>
                                  updateLoan(loan.id, "maturityDate", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="LTV"
                                value={loan.ltv}
                                onChange={(event) =>
                                  updateLoan(loan.id, "ltv", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Points"
                                value={loan.points}
                                onChange={(event) =>
                                  updateLoan(loan.id, "points", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Prepayment Penalty"
                                value={loan.prepaymentPenalty}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "prepaymentPenalty",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </LoanFieldGroup>

                        <LoanFieldGroup
                          title="Parties & Collateral"
                          collapsed={partiesCollapsed}
                          onToggle={() => toggleLoanSection(loan.id, "parties")}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Borrower / Owning Entity"
                                value={loan.borrowerOwningEntity}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "borrowerOwningEntity",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Guarantor"
                                value={loan.guarantor}
                                onChange={(event) =>
                                  updateLoan(loan.id, "guarantor", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Collateral / Secured Property"
                                value={loan.collateralSecuredProperty}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "collateralSecuredProperty",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </LoanFieldGroup>

                        <LoanFieldGroup
                          title="Servicing & Supporting Details"
                          collapsed={servicingCollapsed}
                          onToggle={() => toggleLoanSection(loan.id, "servicing")}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Loan Benefits"
                                value={loan.loanBenefits}
                                onChange={(event) =>
                                  updateLoan(loan.id, "loanBenefits", event.target.value)
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Servicer / Contact Info"
                                value={loan.servicerContactInfo}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "servicerContactInfo",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Related Documents"
                                value={loan.relatedDocuments}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "relatedDocuments",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Escrow / Reserve Requirements"
                                value={loan.escrowReserveRequirements}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "escrowReserveRequirements",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Covenants / Special Conditions"
                                value={loan.covenantsSpecialConditions}
                                onChange={(event) =>
                                  updateLoan(
                                    loan.id,
                                    "covenantsSpecialConditions",
                                    event.target.value
                                  )
                                }
                                sx={LOAN_FIELD_SX}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </LoanFieldGroup>
                      </Stack>

                      <Box sx={{ mt: 1.5 }}>
                        <CollapsibleLoanSection
                          title="Loan Adjustments"
                          collapsed={adjustmentsCollapsed}
                          onToggle={() => toggleLoanSection(loan.id, "adjustments")}
                          onAdd={() => addLoanAdjustment(loan.id)}
                          addTitle="Add loan adjustment"
                        >
                          {loan.adjustments.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                              No loan adjustments yet. Use the plus button to add one.
                            </Typography>
                          ) : (
                            <Stack spacing={1.5}>
                              {loan.adjustments.map((adjustment, adjustmentIndex) => (
                                <Box
                                  key={`${loan.id}-adjustment-${adjustmentIndex}`}
                                  sx={ENTRY_CARD_SX}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                      mb: 1.5,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 12,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                        color: "#475569",
                                      }}
                                    >
                                      {`Adjustment ${adjustmentIndex + 1}`}
                                    </Typography>
                                    <Tooltip title="Delete adjustment">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          deleteLoanAdjustment(loan.id, adjustmentIndex)
                                        }
                                        sx={{
                                          color: "#94a3b8",
                                          "&:hover": {
                                            color: "#dc2626",
                                            backgroundColor: "#fee2e2",
                                          },
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        fullWidth
                                        label="Effective Date"
                                        type="date"
                                        value={adjustment.effectiveDate}
                                        onChange={(event) =>
                                          updateLoanAdjustment(
                                            loan.id,
                                            adjustmentIndex,
                                            "effectiveDate",
                                            event.target.value
                                          )
                                        }
                                        sx={LOAN_FIELD_SX}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        fullWidth
                                        label="Adjusted Interest Rate"
                                        value={adjustment.adjustedInterestRate}
                                        onChange={(event) =>
                                          updateLoanAdjustment(
                                            loan.id,
                                            adjustmentIndex,
                                            "adjustedInterestRate",
                                            event.target.value
                                          )
                                        }
                                        sx={LOAN_FIELD_SX}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        fullWidth
                                        label="Reason"
                                        value={adjustment.reason}
                                        onChange={(event) =>
                                          updateLoanAdjustment(
                                            loan.id,
                                            adjustmentIndex,
                                            "reason",
                                            event.target.value
                                          )
                                        }
                                        sx={LOAN_FIELD_SX}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        label="Notes"
                                        value={adjustment.notes}
                                        onChange={(event) =>
                                          updateLoanAdjustment(
                                            loan.id,
                                            adjustmentIndex,
                                            "notes",
                                            event.target.value
                                          )
                                        }
                                        sx={LOAN_FIELD_SX}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </CollapsibleLoanSection>
                      </Box>

                      <Box sx={{ mt: 1.5 }}>
                        <CollapsibleLoanSection
                          title="Loan Notes"
                          collapsed={notesCollapsed}
                          onToggle={() => toggleLoanSection(loan.id, "notes")}
                          onAdd={() => addLoanNote(loan.id)}
                          addTitle="Add loan note"
                        >
                          {loan.notes.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                              No loan notes yet. Use the plus button to add one.
                            </Typography>
                          ) : (
                            <Stack spacing={1.5}>
                              {loan.notes.map((noteEntry, noteIndex) => (
                                <Box key={`${loan.id}-note-${noteIndex}`} sx={ENTRY_CARD_SX}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                      mb: 1.5,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 12,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                        color: "#475569",
                                      }}
                                    >
                                      {`Note ${noteIndex + 1}`}
                                    </Typography>
                                    <Tooltip title="Delete note">
                                      <IconButton
                                        size="small"
                                        onClick={() => deleteLoanNote(loan.id, noteIndex)}
                                        sx={{
                                          color: "#94a3b8",
                                          "&:hover": {
                                            color: "#dc2626",
                                            backgroundColor: "#fee2e2",
                                          },
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  <Stack spacing={1.5}>
                                    <TextField
                                      fullWidth
                                      label="Date"
                                      type="date"
                                      value={noteEntry.date}
                                      onChange={(event) =>
                                        updateLoanNote(
                                          loan.id,
                                          noteIndex,
                                          "date",
                                          event.target.value
                                        )
                                      }
                                      sx={{ ...LOAN_FIELD_SX, maxWidth: 220 }}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                      fullWidth
                                      multiline
                                      minRows={2}
                                      label="Note"
                                      value={noteEntry.note}
                                      onChange={(event) =>
                                        updateLoanNote(
                                          loan.id,
                                          noteIndex,
                                          "note",
                                          event.target.value
                                        )
                                      }
                                      sx={LOAN_FIELD_SX}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{
                                        style: { resize: "vertical" },
                                      }}
                                    />
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </CollapsibleLoanSection>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
  }
);

export default PropertyLoansTab;
